import { createContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import * as Sentry from '@sentry/react';

// project imports
import useOnboarding from 'hooks/useOnboarding';
import useTTSBusinessAPIPermissions from 'features/permissions/hooks/useTTSBusinessAPIPermissions';

import { PermissionsAlertContextType } from 'model/types/permissions';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { grantTTSPermissions, queryMerchantInfo, setBasicInfo } from 'app/slices/merchantSlice';
import { APP_DEFAULT_PATH } from 'constants/appConstants';
import { TTS_DATA_ANALYTICS_PATH } from 'constants/routeConstants';

export const PermissionsAlertContext = createContext<PermissionsAlertContextType | null>(null);

export function PermissionsAlertProvider({ children }: { readonly children: React.ReactNode }) {
  const location = useLocation();
  const { onboardingCreate } = useOnboarding();
  const { skipped, handleGrantTTSBusinessAPIPermissions, handleSkipTTSBusinessAPIAlert } = useTTSBusinessAPIPermissions();

  const { merchant, ttsPermissionsGranted, tiktokPlan, youtubePlan } = useAppSelector((store) => store.merchant);
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [displayTTSBusinessAPIAlert, setDisplayTTSBusinessAPIAlert] = useState(false);

  useEffect(() => {
    if (!merchant) getMerchantPermissionsData();
  }, []);

  useEffect(() => {
    const businessAccessGranted = merchant?.tiktokBusinessAuthUrl === null;
    const rootOrDashboardPath = location.pathname === APP_DEFAULT_PATH || location.pathname === TTS_DATA_ANALYTICS_PATH;
    const isCustomMerchant = merchant?.hidePlans;
    const skippedByUserPreference = isCustomMerchant ? false : skipped;

    if (rootOrDashboardPath && !businessAccessGranted && !skippedByUserPreference) {
      setDisplayTTSBusinessAPIAlert(true);
    } else {
      setDisplayTTSBusinessAPIAlert(false);
    }
  }, [location.pathname, merchant, skipped]);

  async function getMerchantPermissionsData() {
    try {
      const result = await queryMerchantInfo();
      dispatch(setBasicInfo(result));
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setIsLoading(false);
    }
  }

  const handleGrantTTSPermissions = (closePermissionsModal: () => void) => {
    closePermissionsModal();
    dispatch(grantTTSPermissions({ ttsPermissionsGranted: true, merchant: merchant }));
    onboardingCreate({
      currency: 'USD',
      metadata: JSON.stringify({ permissions_granted: 'true' })
    });
  };

  const contextValue = useMemo(
    () => ({
      displayTTSBusinessAPIAlert,
      isLoading,
      merchant,
      handleGrantTTSBusinessAPIPermissions,
      handleGrantTTSPermissions,
      handleSkipTTSBusinessAPIAlert,
      tiktokPlan,
      ttsPermissionsGranted,
      youtubePlan
    }),
    [
      displayTTSBusinessAPIAlert,
      isLoading,
      merchant,
      handleGrantTTSBusinessAPIPermissions,
      handleGrantTTSPermissions,
      handleSkipTTSBusinessAPIAlert,
      tiktokPlan,
      ttsPermissionsGranted,
      youtubePlan
    ]
  );

  return <PermissionsAlertContext.Provider value={contextValue}>{children}</PermissionsAlertContext.Provider>;
}
